import { useEffect, useRef } from 'react';
import {
  makeStyles,
  Button,
  Container,
  Grid,
  Typography,
  Theme,
  createStyles,
  Hidden,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from '@material-ui/core';
import Video from 'twilio-video';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { getQualityScore, QualityScore } from '../../../utils/getQualityScore';
import { CheckMark } from '../../../icons/CheckMark';
import { CopyIcon } from '../../../icons/CopyIcon';
import { DownloadIcon } from '../../../icons/DownloadIcon';
import { SmallError } from '../../../icons/SmallError';
import { postMessageToParent } from '../../../postMessage/postMessageToParent';
import { QualityScoreLabel, TechCheckEventType } from '../../../postMessage/events';
import { getNetworkPathType } from '../../../utils/getNetworkPathType';
import { RestrictiveNetworkNotice } from '../../RestrictiveNetworkNotice/RestrictiveNetworkNotice';
import { SupportedList } from '../../SupportedBrowsersList/SupportedList';
import { useBranding } from '../../../branding/BrandingProvider';
import SomeFailed from './SomeFailed.png';
import TestsPassed from './TestsPassed.png';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      float: 'left',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
        paddingBottom: '1em',
      },
    },
    buttonContainer: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: '1em',
      width: '100%',
    },
    resultsList: {
      float: 'right',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
      },
    },
    resultContainer: {
      marginTop: '1.5em',
      '&:not(:last-child)': {
        paddingBottom: '1.5em',
        borderBottom: '0.1em solid #8891AA',
      },
      [theme.breakpoints.down('md')]: {
        '&:last-child': {
          paddingBottom: '1.5em',
        },
      },
    },
    iconContainer: {
      display: 'flex',
      '& svg': {
        margin: '0.2em 0.8em 0 0',
      },
    },
    downloadButton: {
      '& svg': {
        position: 'relative',
        left: '-5px',
      },
    },
    restartButton: {
      backgroundColor: '#FFFFFF',
      borderColor: '#8891AA',
    },
    illustration: {
      marginTop: '7em',
      width: '245px',
      height: 'auto',
      borderRadius: '16px',
    },
    gutterBottom: {
      marginBottom: '1em',
      [theme.breakpoints.down('md')]: {
        marginBottom: '1.5em',
      },
    },
    paper: {
      padding: '1.2em',
      marginTop: '0.5em',
      maxWidth: '400px',
      borderRadius: '8px',
      '& li': {
        '&:not(:last-child)': {
          marginBottom: '1em',
        },
      },
    },
    copyButton: {
      borderLeft: 'solid 1px #E1E3EA',
      maxHeight: '2.5em',
      display: 'flex',
    },
    urlTextBox: {
      marginTop: '1em',
      maxWidth: '400px',
    },
    caption: {
      display: 'block',
      marginTop: '0.8em',
    },
  })
);

export function Results() {
  const { state, downloadFinalTestResults, getFinalTestResults } = useAppStateContext();
  const { totalQualityScore } = getQualityScore(state.preflightTest.report, state.bitrateTest.report);
  const { appName } = useBranding();
  const classes = useStyles();

  const networkPathType = getNetworkPathType(state.preflightTest.report);

  // Each of the three background checks, evaluated together so the single
  // Results pane can present one definitive pass/fail outcome.
  const browserSupported = Video.isSupported;
  const connectivityFailed =
    state.preflightTest.error !== null ||
    !state.preflightTest.signalingGatewayReachable ||
    !state.preflightTest.turnServersReachable;
  const qualityPassed = totalQualityScore === QualityScore.Excellent || totalQualityScore === QualityScore.Good;

  const testsPassed = browserSupported && !connectivityFailed && qualityPassed;
  const qualityScore = QualityScore[totalQualityScore].toLowerCase();

  const appURL = window.location.href;
  const copyAppLink = () => {
    navigator.clipboard.writeText(appURL);
  };

  // Emit the terminal `complete` event (overall pass/fail + full results JSON)
  // to the embedding parent window exactly once, when the user actually reaches
  // the Results pane. NOTE: every pane is mounted up-front by MainContent (they
  // are only visually hidden), so we must gate on `activePane` rather than on
  // mount — otherwise `complete` would fire immediately on app load.
  const completeSent = useRef(false);
  useEffect(() => {
    if (completeSent.current) return;
    if (state.activePane !== ActivePane.Results) return;
    completeSent.current = true;
    postMessageToParent(TechCheckEventType.Complete, {
      passed: testsPassed,
      score: QualityScore[totalQualityScore] as QualityScoreLabel,
      networkPath: networkPathType,
      results: getFinalTestResults(),
    });
  }, [state.activePane, testsPassed, totalQualityScore, getFinalTestResults, networkPathType]);

  const restartTest = () => {
    postMessageToParent(TechCheckEventType.Restarted);
    window.location.reload();
  };

  if (testsPassed) {
    return (
      <Container>
        <div>
          <Grid item lg={5} className={classes.header}>
            <Typography variant="h1" gutterBottom>
              All tests passed!
            </Typography>

            <Typography variant="body1" gutterBottom>
              As far as we can tell, your video should be working. If you're still experiencing issues, restart the
              test.
            </Typography>

            <div className={classes.buttonContainer}>
              <Button variant="outlined" className={classes.restartButton} onClick={restartTest}>
                Restart test
              </Button>
            </div>

            <Hidden mdDown>
              <img src={TestsPassed} alt="Success" className={classes.illustration} />
            </Hidden>
          </Grid>

          <Grid item lg={5} className={classes.resultsList}>
            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Device &amp; Browser Setup
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                Audio and video successfully received from your hardware and your browser is supported.
              </Typography>
            </div>

            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Connectivity
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                All connections are working successfully.
              </Typography>
            </div>

            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Quality &amp; Performance
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                Awesome! Your expected call quality is <strong>{qualityScore}</strong> and overall performance looks
                {qualityScore === 'excellent' ? ' good' : ' ok'}.
              </Typography>
            </div>

            <RestrictiveNetworkNotice pathType={networkPathType} />
          </Grid>
        </div>
      </Container>
    );
  }

  // Failure: show one consolidated screen listing each check with its status
  // plus the actionable detail for whatever failed.
  return (
    <Container>
      <div>
        <Grid item lg={5} className={classes.header}>
          <Typography variant="h1" gutterBottom>
            Some tests failed
          </Typography>

          <Typography variant="body1" gutterBottom>
            Use the list to solve common video issues and restart the test. If you can’t easily solve the problem(s),
            download the report results and send them to your network administrator.
          </Typography>

          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              className={classes.downloadButton}
              onClick={downloadFinalTestResults}
            >
              <DownloadIcon />
              Download report results
            </Button>
            <Button variant="outlined" className={classes.restartButton} onClick={restartTest}>
              Restart test
            </Button>
          </div>

          <Hidden mdDown>
            <img src={SomeFailed} alt="Some Failed" className={classes.illustration} />
          </Hidden>
        </Grid>

        <Grid item lg={5} className={classes.resultsList}>
          {/* Browser support */}
          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {browserSupported ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" className={classes.gutterBottom}>
                Browser Compatibility
              </Typography>
            </div>

            {browserSupported ? (
              <Typography variant="body1" className={classes.gutterBottom}>
                Your browser is supported.
              </Typography>
            ) : (
              <>
                <Typography variant="body1" className={classes.gutterBottom}>
                  Your browser isn't supported. Download and install one from the list below, then copy the URL and
                  paste it into your new browser to restart the test.
                </Typography>
                <Paper className={classes.paper}>
                  <SupportedList />
                </Paper>
                <Typography variant="caption" className={classes.caption}>
                  We support the most recent and the two previous versions. Please{' '}
                  <Link
                    color="inherit"
                    underline="always"
                    target="_blank"
                    rel="noopener"
                    href="https://www.twilio.com/docs/video/javascript#supported-browsers"
                  >
                    visit our docs
                  </Link>{' '}
                  for more details.
                </Typography>
                <div className={classes.urlTextBox}>
                  <TextField
                    fullWidth={true}
                    id="read-only-app-link"
                    defaultValue={appURL}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <div className={classes.copyButton}>
                            <IconButton edge="end" onClick={copyAppLink}>
                              <CopyIcon />
                            </IconButton>
                          </div>
                        </InputAdornment>
                      ),
                      readOnly: true,
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Connectivity */}
          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {connectivityFailed ? <SmallError /> : <CheckMark />}
              <Typography variant="h3" className={classes.gutterBottom}>
                Connectivity
              </Typography>
            </div>

            {connectivityFailed ? (
              <>
                <Typography variant="body1" className={classes.gutterBottom}>
                  It's possible that you're behind a firewall that will need to be updated by your network
                  administrator. To conduct a video call, your internet needs to be able to communicate with {appName}'s
                  cloud.
                </Typography>
                <Typography variant="body1" className={classes.gutterBottom}>
                  Learn{' '}
                  <Link
                    href="https://www.twilio.com/docs/video/ip-addresses"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="always"
                  >
                    how to configure your firewall correctly
                  </Link>
                  , then download the report results to send to your network admin and re-run this test.
                </Typography>
              </>
            ) : (
              <Typography variant="body1" className={classes.gutterBottom}>
                All connections are working successfully.
              </Typography>
            )}
          </div>

          {/* Quality & Performance */}
          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {qualityPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" className={classes.gutterBottom}>
                Quality &amp; Performance
              </Typography>
            </div>

            {qualityPassed ? (
              <Typography variant="body1" className={classes.gutterBottom}>
                Your expected call quality is <strong>{qualityScore}</strong>.
              </Typography>
            ) : (
              <>
                <Typography variant="body1" className={classes.gutterBottom}>
                  Your overall score is <strong>{qualityScore}</strong>, which means your connection may not be good
                  enough to run video properly. Try these tips and rerun the test.
                </Typography>
                <Paper className={classes.paper}>
                  <Typography variant="body1">
                    <strong>Tips to improve quality and performance:</strong>
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant="body1">
                        Close unused tabs and applications on your computer/device when using video.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body1">Move closer to your router.</Typography>
                    </li>
                    <li>
                      <Typography variant="body1">Turn off or disconnect from VPN.</Typography>
                    </li>
                    <li>
                      <Typography variant="body1">Restart your computer or device.</Typography>
                    </li>
                  </ul>
                </Paper>
              </>
            )}
          </div>

          <RestrictiveNetworkNotice pathType={networkPathType} />
        </Grid>
      </div>
    </Container>
  );
}
